import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { ErrorState, LoadingState } from "@/components/States";
import { useAuth } from "@/lib/AuthContext";
import {
  executionsApi,
  manufacturingOrdersApi,
  ordersApi,
  stagesApi,
} from "@/lib/api";
import { formatRelative, shortId } from "@/lib/format";
import { useEventStream } from "@/lib/ws";

export default function Dashboard() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  // Производственник (видит стадии/задания/выполнения) vs офис продаж
  // (видит только заказы на производство + документы). Гейт — право
  // исполнения стадий: оно есть у операторов/мастеров/начпроизводства/админа,
  // но НЕ у SALES_OFFICE / DOC_OPERATOR.
  const isProduction = hasPermission("stage.execute");
  const canMo = hasPermission("manufacturing_order.read");

  // Один live-стрим на дашборд (производственный). Для офиса не подписываемся.
  const { events, connected } = useEventStream(
    { kind: "global" },
    { maxBuffer: 30, enabled: isProduction },
  );

  return (
    <>
      <PageHeader
        eyebrow="overview"
        title={t("dashboard.title")}
        description={
          isProduction
            ? t("dashboard.description")
            : t("dashboard.descriptionOffice")
        }
        actions={
          isProduction ? (
            <span
              className={`pill ${connected ? "pill-ok" : "pill-danger"}`}
              title={t("dashboard.wsSubscription")}
            >
              {connected ? "live" : "offline"}
            </span>
          ) : undefined
        }
      />

      <SectionsRow isProduction={isProduction} />

      {isProduction ? (
        <ProductionDashboard events={events} />
      ) : canMo ? (
        <OfficeDashboard />
      ) : null}
    </>
  );
}

// ───────── Производственный дашборд (как было) ─────────

function ProductionDashboard({ events }: { events: DashboardEvents }) {
  const { t } = useTranslation();
  const stages = useQuery({
    queryKey: ["stages", "list"],
    queryFn: () => stagesApi.list({ page_size: 100 }),
  });
  const orders = useQuery({
    queryKey: ["orders", "recent"],
    queryFn: () => ordersApi.list({ page_size: 10 }),
  });
  const executions = useQuery({
    queryKey: ["executions", "recent"],
    queryFn: () => executionsApi.list({ page_size: 10 }),
    refetchInterval: 5000,
  });

  return (
    <>
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi label={t("dashboard.kpiStages")} value={stages.data?.pagination.total ?? "—"} hint={t("dashboard.kpiStagesHint")} />
        <Kpi label={t("dashboard.kpiActiveOrders")} value={orders.data?.items.filter((o) => o.status === "ACTIVE" || o.status === "ADJUSTED").length ?? "—"} hint="ACTIVE + ADJUSTED" />
        <Kpi label={t("dashboard.kpiOpenExecutions")} value={executions.data?.items.filter((e) => e.status === "IN_PROGRESS" || e.status === "QC_PENDING" || e.status === "STARTED").length ?? "—"} hint={t("dashboard.kpiOpenExecutionsHint")} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5">
          <h2 className="text-base text-text-heading flex items-center justify-between">
            {t("dashboard.recentOrders")}
            <a href="/orders" className="text-xs font-normal text-accent-strong hover:underline">{t("dashboard.all")}</a>
          </h2>
          <div className="mt-4 -mx-2">
            {orders.isLoading ? (
              <LoadingState />
            ) : orders.isError ? (
              <ErrorState error={orders.error} />
            ) : orders.data!.items.length === 0 ? (
              <p className="px-2 text-text-muted text-sm">{t("dashboard.noOrders")}</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-text-muted font-mono text-xs uppercase">
                    <th className="px-2 py-2">{t("dashboard.colCode")}</th>
                    <th className="px-2 py-2">{t("dashboard.colStage")}</th>
                    <th className="px-2 py-2">{t("dashboard.colPlan")}</th>
                    <th className="px-2 py-2">{t("dashboard.colStatus")}</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.data!.items.map((o) => (
                    <tr key={o.id} className="border-t border-border">
                      <td className="px-2 py-2 font-mono">{o.code}</td>
                      <td className="px-2 py-2 text-text-secondary">STAGE_{o.stage_id}</td>
                      <td className="px-2 py-2 text-text-secondary">
                        {o.completed_quantity}/{o.target_quantity}
                        {o.rejected_quantity > 0 && (
                          <span className="text-danger ml-2">·{o.rejected_quantity}</span>
                        )}
                      </td>
                      <td className="px-2 py-2"><StatusPill kind="order" value={o.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <LiveEventsPanel events={events} />
      </section>
    </>
  );
}

type DashboardEvents = ReturnType<typeof useEventStream>["events"];

function LiveEventsPanel({ events }: { events: DashboardEvents }) {
  const { t } = useTranslation();
  return (
    <div className="card p-5">
      <h2 className="text-base text-text-heading flex items-center justify-between">
        {t("dashboard.liveEvents")} <span className="text-xs font-normal text-text-muted">{events.length}</span>
      </h2>
      <div className="mt-4 max-h-[360px] overflow-y-auto space-y-1 font-mono text-xs">
        {events.length === 0 ? (
          <p className="text-text-muted">{t("dashboard.waitingEvents")}</p>
        ) : (
          events.map((ev, i) => (
            <div
              key={i}
              className="flex items-baseline gap-3 py-1.5 border-b border-border/60 last:border-0"
            >
              <span className="text-text-muted shrink-0 w-20 tabular-nums">
                {formatRelative(ev.occurred_at)}
              </span>
              <span className="text-accent-strong shrink-0">{ev.type}</span>
              <span className="text-text-secondary truncate">
                {ev.order_id && `o:${shortId(ev.order_id)}`}
                {ev.record_id && ` e:${shortId(ev.record_id)}`}
                {ev.station_id && ` @${ev.station_id}`}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ───────── Офисный дашборд (SALES_OFFICE / DOC_OPERATOR) ─────────

function OfficeDashboard() {
  const { t } = useTranslation();
  const processes = useQuery({
    queryKey: ["manufacturing-orders", "processes"],
    queryFn: () => manufacturingOrdersApi.processes(),
    staleTime: 60_000,
  });
  const processName = (key: string | null) =>
    processes.data?.find((p) => p.key === key)?.name ?? key ?? "—";
  const mo = useQuery({
    queryKey: ["manufacturing-orders", "recent"],
    queryFn: () => manufacturingOrdersApi.list({ page_size: 10 }),
  });
  const items = mo.data?.items ?? [];
  const total = mo.data?.pagination.total ?? "—";
  const inProd = items.filter((o) => o.status === "IN_PRODUCTION").length;
  const draft = items.filter((o) => o.status === "DRAFT" || o.status === "READY").length;

  return (
    <>
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi label={t("dashboard.kpiMoTotal")} value={total} hint={t("dashboard.kpiMoTotalHint")} />
        <Kpi label={t("dashboard.kpiMoInProduction")} value={inProd} hint="IN_PRODUCTION" />
        <Kpi label={t("dashboard.kpiMoDraft")} value={draft} hint="DRAFT + READY" />
      </section>

      <section className="card p-5">
        <h2 className="text-base text-text-heading flex items-center justify-between">
          {t("dashboard.recentMoOrders")}
          <Link to="/manufacturing-orders" className="text-xs font-normal text-accent-strong hover:underline">{t("dashboard.all")}</Link>
        </h2>
        <div className="mt-4 -mx-2">
          {mo.isLoading ? (
            <LoadingState />
          ) : mo.isError ? (
            <ErrorState error={mo.error} />
          ) : items.length === 0 ? (
            <p className="px-2 text-text-muted text-sm">{t("dashboard.noMoOrders")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-text-muted font-mono text-xs uppercase">
                  <th className="px-2 py-2">{t("dashboard.colCode")}</th>
                  <th className="px-2 py-2">{t("dashboard.colArticle")}</th>
                  <th className="px-2 py-2">{t("dashboard.colProcess")}</th>
                  <th className="px-2 py-2">{t("dashboard.colStatus")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => (
                  <tr key={o.id} className="border-t border-border">
                    <td className="px-2 py-2">
                      <Link to={`/manufacturing-orders/${o.id}`} className="font-mono text-accent-strong hover:underline">{o.code}</Link>
                    </td>
                    <td className="px-2 py-2 font-mono text-text-secondary">{o.article ?? "—"}</td>
                    <td className="px-2 py-2 text-text-secondary">{processName(o.process_key)}</td>
                    <td className="px-2 py-2"><StatusPill kind="manufacturing" value={o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </>
  );
}

function Kpi({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div className="card p-5">
      <p className="text-text-muted text-xs uppercase tracking-wider font-mono">
        {label}
      </p>
      <p className="text-4xl font-serif text-text-h1 mt-2 tabular-nums">{value}</p>
      {hint && <p className="text-text-muted text-xs mt-1">{hint}</p>}
    </div>
  );
}

// Section quick-access — гейтятся по роли: производственные разделы
// (стадии / задания на стадию / потоки / трассировка) видит только тот,
// у кого есть право исполнения стадий; офису остаются заказы на
// производство, документы и печать.
function SectionsRow({ isProduction }: { isProduction: boolean }) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const tiles: { to: string; icon: string; label: string; show: boolean }[] = [
    { to: "/stages", icon: "🪜", label: t("dashboard.tileStages"), show: isProduction },
    // Два уровня заказов — явные раздельные подписи (не путать):
    //   «Заказы на производство» = заголовочный документ (manufacturing_orders)
    //   «Задания на стадию»      = исполнение (production_orders, /orders)
    { to: "/manufacturing-orders", icon: "🏷", label: t("dashboard.tileManufacturingOrders"), show: hasPermission("manufacturing_order.read") },
    { to: "/orders", icon: "📋", label: t("dashboard.tileOrders"), show: isProduction },
    { to: "/flows", icon: "📦", label: t("dashboard.tileFlows"), show: isProduction },
    { to: "/traceability", icon: "🔎", label: t("dashboard.tileTraceability"), show: isProduction },
    { to: "/admin/documents", icon: "📄", label: t("dashboard.tileDocuments"), show: hasPermission("document.print") },
    { to: "/print", icon: "🖨", label: t("dashboard.tilePrint"), show: hasPermission("print.use") },
  ];
  const visible = tiles.filter((tile) => tile.show);
  return (
    <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {visible.map((tile) => (
        <Link
          key={tile.to}
          to={tile.to}
          className="card px-4 py-3 flex items-center gap-3 hover:border-accent transition-colors"
        >
          <span className="text-xl select-none" aria-hidden>
            {tile.icon}
          </span>
          <span className="text-sm font-medium">{tile.label}</span>
        </Link>
      ))}
    </section>
  );
}
